import type { DeepActivityEvent } from "@/stores/ai-explorer-store"

const FILE_PATH_KEYS = [
  "path",
  "file_path",
  "filepath",
  "filename",
  "file",
  "file_name",
  "query",
] as const

export interface TodoCounts {
  pending: number
  in_progress: number
  done: number
  total: number
}

interface TodoLike {
  content?: unknown
  text?: unknown
  status?: unknown
}

function isTodoLike(value: unknown): value is TodoLike {
  return !!value && typeof value === "object"
}

function todoLabel(t: TodoLike): string {
  let content = ""
  if (typeof t.content === "string") {
    content = t.content
  } else if (typeof t.text === "string") {
    content = t.text
  }
  return content.trim()
}

function todoStatus(t: TodoLike): string {
  return typeof t.status === "string" ? t.status : "pending"
}

export function readTodosFromEvent(event: DeepActivityEvent | null | undefined): TodoLike[] | null {
  if (!event) return null
  const raw = event.input_summary?.todos
  if (!Array.isArray(raw) || raw.length === 0) return null
  return raw.filter(isTodoLike)
}

export function summarizeTodos(todos: TodoLike[]): TodoCounts {
  const counts: TodoCounts = { pending: 0, in_progress: 0, done: 0, total: todos.length }
  for (const t of todos) {
    const status = todoStatus(t)
    if (status === "in_progress") counts.in_progress += 1
    else if (status === "done" || status === "completed") counts.done += 1
    else counts.pending += 1
  }
  return counts
}

export interface TodoDiff {
  added: string[]
  completed: string[]
  started: string[]
  removed: string[]
}

export function diffTodos(prev: TodoLike[] | null | undefined, next: TodoLike[]): TodoDiff {
  const prevByLabel = new Map<string, TodoLike>()
  for (const t of prev ?? []) {
    const label = todoLabel(t)
    if (label) prevByLabel.set(label, t)
  }
  const nextByLabel = new Map<string, TodoLike>()
  for (const t of next) {
    const label = todoLabel(t)
    if (label) nextByLabel.set(label, t)
  }
  const added: string[] = []
  const completed: string[] = []
  const started: string[] = []
  for (const [label, nextTodo] of nextByLabel) {
    const prevTodo = prevByLabel.get(label)
    const nextStatus = todoStatus(nextTodo)
    if (!prevTodo) {
      added.push(label)
      continue
    }
    const prevStatus = todoStatus(prevTodo)
    const wasDone = prevStatus === "done" || prevStatus === "completed"
    const isDone = nextStatus === "done" || nextStatus === "completed"
    if (!wasDone && isDone) {
      completed.push(label)
    } else if (prevStatus !== "in_progress" && nextStatus === "in_progress") {
      started.push(label)
    }
  }
  const removed: string[] = []
  for (const label of prevByLabel.keys()) {
    if (!nextByLabel.has(label)) removed.push(label)
  }
  return { added, completed, started, removed }
}

export function extractTaskBrief(start: DeepActivityEvent | null | undefined): string | null {
  if (!start) return null
  const raw = start.input_summary?.description ?? start.input_summary?.query
  if (typeof raw !== "string") return null
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null
  return trimmed.length > 4000 ? trimmed.slice(0, 4000) + "…" : trimmed
}

const HIGHLIGHTED_ARTIFACT_PATHS = new Set(["/plan.md", "/deliverable.md"])

export type HighlightedArtifact = "plan" | "deliverable" | null

export function classifyArtifactPath(path: string | null | undefined): HighlightedArtifact {
  if (!path) return null
  const normalised = path.startsWith("/") ? path : `/${path}`
  if (!HIGHLIGHTED_ARTIFACT_PATHS.has(normalised)) return null
  return normalised === "/plan.md" ? "plan" : "deliverable"
}

export function extractFilePath(start: DeepActivityEvent | null | undefined): string | null {
  if (!start) return null
  const summary = start.input_summary
  if (!summary) return null
  for (const key of FILE_PATH_KEYS) {
    const v = summary[key]
    if (typeof v === "string" && v.length > 0) return v
  }
  return null
}
